/**
 * Reading a stored point back out.
 *
 * The organiser form writes `{ lat, lng }`, but a point that has been through a
 * spatial column can come back as WKT ("POINT(77.59 12.97)" — longitude first),
 * as GeoJSON, or as a plain "lat,lng" string, and which one arrives depends on
 * how the public data source exposes the column rather than on anything this
 * page controls.
 *
 * So every shape that could plausibly arrive is accepted, and anything else
 * returns null — which draws no map at all. A wrong map is worse than none: a
 * start line in the wrong hemisphere is a confident lie, and a runner has no
 * way to tell it from the truth.
 */

const isNum = value => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value));

/** Latitude and longitude have real bounds; anything outside them is not a point. */
function validate(lat, lng) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!isNum(lat) || !isNum(lng)) return null;
  if (latitude < -90 || latitude > 90) return null;
  if (longitude < -180 || longitude > 180) return null;
  return { lat: latitude, lng: longitude };
}

// POINT(lng lat) — the order WKT uses, which is the reverse of how the rest of
// this file reads. Worth naming, because silently swapping them would put every
// Bengaluru start line in the Indian Ocean.
const WKT_RE = /^\s*(?:SRID=\d+;)?\s*POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)\s*$/i;

export default function parsePoint(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const wkt = WKT_RE.exec(value);
    if (wkt) return validate(wkt[2], wkt[1]);

    const pair = value.split(',');
    if (pair.length === 2) return validate(pair[0], pair[1]);

    try {
      return parsePoint(JSON.parse(value));
    } catch (e) {
      return null;
    }
  }

  if (Array.isArray(value)) {
    // GeoJSON coordinates are [lng, lat]; a hand-written pair is [lat, lng].
    // They cannot be told apart, so only the explicit GeoJSON object form is
    // read as GeoJSON and a bare array is taken at its more common meaning.
    return value.length === 2 ? validate(value[0], value[1]) : null;
  }

  if (typeof value === 'object') {
    if (value.type === 'Point' && Array.isArray(value.coordinates)) {
      return validate(value.coordinates[1], value.coordinates[0]);
    }
    const lat = value.lat ?? value.latitude ?? value.y;
    const lng = value.lng ?? value.lon ?? value.longitude ?? value.x;
    return validate(lat, lng);
  }

  return null;
}
